import { Sync, ViewSidebar } from '@mui/icons-material';
import { Box, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { reaction } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { timelogsApi } from '@/api/timelogsApi';
import { yandexTrackerApi } from '@/api/yandexTrackerApi';
import { TimesheetControls } from '@/components/TimesheetControls';
import { routerUrls } from '@/configs/routes/routerUrls';
import { useUserStore } from '@/configs/store';
import { ViewTable } from '@/configs/ViewTable';
import { AttendeeLogCheckerModel } from '@/features/calendarView/store/AttendeeLogCheckerModel';
import {
  CalendarViewProvider,
  useCalendarViewStore,
} from '@/features/calendarView/store/CalendarViewContext';
import type { CalendarEventWithColor } from '@/features/calendarView/types';
import { AttendeeLogCheckerPanel } from '@/features/calendarView/ui/AttendeeLogCheckerPanel';
import { CalDavRequiredOverlay } from '@/features/calendarView/ui/CalDavRequiredOverlay';
import { CalendarSidebar } from '@/features/calendarView/ui/CalendarSidebar';
import { CalendarViewWidget } from '@/features/calendarView/ui/CalendarViewWidget';
import { EventDetailsModal } from '@/features/calendarView/ui/EventDetailsModal';
import { NoCalendarsSelectedNotice } from '@/features/calendarView/ui/NoCalendarsSelectedNotice';
import { OtherLogsDetailPanel } from '@/features/calendarView/ui/OtherLogsDetailPanel';
import {
  DraftForm,
  DraftTimelogProvider,
  DraftWarningBanner,
  useDraftTimelogStore,
} from '@/features/draftTimelogs';
import { FavoriteUsersProvider } from '@/features/favoriteUsers';
import { GlobalModals } from '@/features/GlobalModals';
import { GlobalUserFilterProvider, useGlobalUserFilterModel } from '@/features/globalUserFilter';
import { AddLog } from '@/features/log/form/AddLog';
import { EditLog } from '@/features/log/form/EditLog';
import { DataValues } from '@/features/log/form/types';
import { getLogsUserId, LogsMode, useIsLogsReadOnly, useLogsMode } from '@/features/logsMode';
import { modalManagerStore } from '@/features/ModalManagerStore';
import { ReadonlyGuard } from '@/features/ReadonlyGuard';
import { TaskStoreProvider, useTaskStore } from '@/features/Task';
import { TimeStatsProvider } from '@/features/TimeStatsModel';
import { TimeLogStoreProvider, useTimeLogStore } from '@/pages/TimesheetsPage/model';
import { AddLogButton, StatsButton } from '@/pages/TimesheetsPage/ui/Buttons';
import type { WeeklyWorkLog } from '@/types';
import { YandexUser } from '@/types/yandex';
import { formatDurationIso } from '@/utils/formatDurationIso';

import s from './CalendarPage.module.scss';

const CalendarDateSync: React.FC = () => {
  const calendarViewStore = useCalendarViewStore();
  const timeLogStore = useTimeLogStore();

  useEffect(() => {
    return reaction(
      () => calendarViewStore.dateNavigation.currentDate.toISO(),
      (dateISO) => {
        if (dateISO) {
          timeLogStore.setCurrentDate(DateTime.fromISO(dateISO));
        }
      },
    );
  }, [calendarViewStore, timeLogStore]);

  return null;
};

const CalendarDraftSync: React.FC = () => {
  const calendarViewStore = useCalendarViewStore();
  const draftStore = useDraftTimelogStore();

  useEffect(() => {
    return reaction(
      () => draftStore.drafts.slice(),
      (drafts) => {
        calendarViewStore.setDrafts(drafts);
      },
      { fireImmediately: true },
    );
  }, [calendarViewStore, draftStore]);

  return null;
};

const CalendarFavoriteTasksLoader: React.FC = () => {
  const { loadFavoriteTasks } = useTaskStore();

  useEffect(() => {
    loadFavoriteTasks();
  }, [loadFavoriteTasks]);

  return null;
};

/** Создаёт логи (за каждого из usersIds) и возвращает их id и начало */
const createWorklogRequest = async ({
  data,
  mode,
}: {
  data: DataValues;
  mode: LogsMode;
}): Promise<{ id: string; start: string }[]> => {
  if (mode === LogsMode.v2) {
    const calendarEvent = data.eventUid
      ? { uid: data.eventUid, recurrenceId: data.eventRecurrenceId ?? null }
      : null;
    const owners = data.usersIds.length ? data.usersIds : [undefined];

    return timelogsApi.createTimelogs(
      owners.map((user) => ({
        task: data.taskKey,
        start: data.startDate,
        duration: formatDurationIso(data.duration),
        comment: data.comment,
        user,
        calendarEvent,
      })),
    );
  }

  const result = await yandexTrackerApi.createWorklog({
    taskKey: data.taskKey,
    duration: `PT${data.duration}M`,
    comment: data.comment,
    startDate: data.startDate,
    userIds: data.usersIds,
    eventUid: data.eventUid,
    eventRecurrenceId: data.eventRecurrenceId,
  });

  return result.items.map((log) => ({ id: String(log.id), start: log.start }));
};

/** Внутренний контент страницы календаря — должен быть внутри GlobalUserFilterProvider */
const CalendarPageBody: React.FC = observer(() => {
  const store = useCalendarViewStore();
  const { user } = useUserStore();
  const { isViewingSelf } = useGlobalUserFilterModel();
  const mode = useLogsMode();
  const isLogsReadOnly = useIsLogsReadOnly();
  // Черновики и создание по выделению — только свои и только когда в учёт можно писать
  const canWriteOwn = isViewingSelf && !isLogsReadOnly;
  const navigate = useNavigate();
  const [showNoCalendarsNotice, setShowNoCalendarsNotice] = useState(true);

  const myUserId = user ? getLogsUserId({ user, mode }) : null;

  const logCheckerModel = useMemo(() => new AttendeeLogCheckerModel(mode), [mode]);

  useEffect(() => {
    if (myUserId) {
      store.setUserId(myUserId);
    }

    store.init();
  }, [store, myUserId]);

  const handleEventClick = useCallback(
    (event: CalendarEventWithColor) => {
      if (event.isDraft && event.draftData) {
        store.openDraftEdit(event.draftData);
      } else if (event.collapsedGroup) {
        store.selectLogGroup(event.collapsedGroup);
      } else if (event.isOtherLog && event.otherLogData) {
        // Только просмотр: клик по логу не открывает правку
        if (!isLogsReadOnly) store.openEditLog(event.otherLogData);
      } else {
        store.selectEvent(event);
      }
    },
    [store, isLogsReadOnly],
  );

  const handleAddLogClick = useCallback(
    (date: DateTime) => {
      store.openQuickAdd(date);
    },
    [store],
  );

  const handleLogTime = useCallback(
    (event: CalendarEventWithColor, forAll: boolean) => {
      store.openLogForm(event, forAll);
    },
    [store],
  );

  const handleDeleteWorklog = useCallback(
    (event: CalendarEventWithColor) => store.deleteWorklog(event),
    [store],
  );

  useEffect(() => {
    if (store.logFormDefaults) {
      logCheckerModel.initWithDate(store.logFormDefaults.startDate);
    } else {
      logCheckerModel.reset();
    }
  }, [store.logFormDefaults, logCheckerModel]);

  const handleUsersChange = useCallback(
    (users: YandexUser[]) => {
      logCheckerModel.setAvailableUsers(users);
    },
    [logCheckerModel],
  );

  const handleTaskKeyChange = useCallback(
    (taskKey: string | null) => {
      logCheckerModel.setTaskKey(taskKey);
    },
    [logCheckerModel],
  );

  const handleLogFormSubmit = useCallback(
    async (data: DataValues) => {
      const logFormEvent = store.logFormEvent;

      // Pass event UID and recurrence ID so the backend can link worklogs to attendee calendar events
      const requestData = logFormEvent?.externalId
        ? {
            ...data,
            eventUid: logFormEvent.externalId,
            eventRecurrenceId: logFormEvent.recurrenceId,
          }
        : data;

      const created = await createWorklogRequest({ data: requestData, mode });

      if (logFormEvent && created.length > 0) {
        const createdLog = created[0];
        store.addCreatedWorklog(createdLog.id, data.duration, data.taskKey, createdLog.start);
      }

      store.closeLogForm();
      logCheckerModel.reset();
      await store.loadWeekData();
    },
    [store, logCheckerModel, mode],
  );

  const handleSimpleAddSubmit = useCallback(
    async (data: DataValues) => {
      await createWorklogRequest({ data, mode });
      await store.loadWeekData();
    },
    [store, mode],
  );

  const handleEditOtherLog = useCallback(
    (log: WeeklyWorkLog) => {
      if (isLogsReadOnly) return;

      store.clearLogGroup();
      store.openEditLog(log);
    },
    [store, isLogsReadOnly],
  );

  const handleEditWorklog = useCallback(
    (event: CalendarEventWithColor) => {
      const log: WeeklyWorkLog = {
        id: event.worklogId ?? '',
        taskKey: event.matchedWorklog?.taskKey ?? '',
        taskTitle: event.summary,
        duration: '',
        durationMinutes: event.matchedWorklog?.duration ?? 0,
        comment: event.matchedWorklog?.comment ?? '',
        date: DateTime.fromISO(event.startsAt).toISODate() ?? '',
        createdAt: '',
        start: event.matchedWorklog?.start ?? event.startsAt,
      };

      store.clearSelectedEvent();
      store.openEditLog(log);
    },
    [store],
  );

  const handleEditLogSubmit = useCallback(
    async (data: DataValues & { workLogId: string; startDate?: string; usersIds?: string[] }) => {
      const durationString = `PT${data.duration}M`;

      if (mode === LogsMode.v2) {
        await timelogsApi.updateTimelog({
          id: data.workLogId,
          data: {
            duration: formatDurationIso(data.duration),
            comment: data.comment,
            // '' — дату в форме не меняли
            start: data.startDate || undefined,
          },
        });
      } else if (data.startDate) {
        // Yandex Tracker API не поддерживает изменение start через PATCH,
        // поэтому создаём новый лог и удаляем старый
        await yandexTrackerApi.createWorklog({
          taskKey: data.taskKey,
          duration: durationString,
          comment: data.comment,
          startDate: data.startDate,
          userIds: data.usersIds,
        });
        await yandexTrackerApi.deleteWorkLog({
          taskKey: data.taskKey,
          workLogId: data.workLogId,
        });
      } else {
        await yandexTrackerApi.updateWorkLog({
          taskKey: data.taskKey,
          workLogId: data.workLogId,
          duration: durationString,
          comment: data.comment,
        });
      }

      store.closeEditLog();
      await store.loadWeekData();
    },
    [store, mode],
  );

  return (
    <TimeLogStoreProvider>
      <DraftTimelogProvider>
        <TimeStatsProvider>
          <TaskStoreProvider>
            <CalendarDateSync />
            <CalendarDraftSync />
            <CalendarFavoriteTasksLoader />
            <Box className={s.calendar_page}>
              {/* Header */}
              <div className={s.calendar_page__header}>
                <TimesheetControls
                  dateNavigation={store.dateNavigation}
                  allowedViews={[ViewTable.Week]}
                  syncConfig={{
                    onSync: () => store.refreshData(),
                    tooltip: 'Обновить календарь',
                    isBusy: store.isBusy,
                    menuItems: [
                      {
                        label: 'Синхронизировать события',
                        icon: <Sync fontSize="small" />,
                        onClick: () => store.syncAndRefresh(),
                      },
                    ],
                  }}
                  actionsChildren={
                    <>
                      <StatsButton />
                      <ReadonlyGuard>
                        <AddLogButton />
                      </ReadonlyGuard>
                    </>
                  }
                />
              </div>
              <GlobalModals />

              {/* Calendar body: loading / error / content */}
              {store.isLoading ? (
                <Box className={s.calendar_page__loading}>
                  <CircularProgress />
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                    Загрузка календаря...
                  </Typography>
                </Box>
              ) : store.isError ? (
                <Box className={s.calendar_page__error}>
                  <Typography variant="h6" color="error">
                    Ошибка загрузки
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {store.error || 'Не удалось загрузить данные календаря'}
                  </Typography>
                </Box>
              ) : (
                <Box className={s.calendar_page__body}>
                  <Box className={s.calendar_page__sidebar_area}>
                    <Tooltip
                      title={store.sidebarCollapsed ? 'Показать календари' : 'Скрыть календари'}
                      arrow
                    >
                      <IconButton
                        size="small"
                        onClick={store.toggleSidebar}
                        color={store.sidebarCollapsed ? 'default' : 'primary'}
                      >
                        <ViewSidebar fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <CalendarSidebar
                      collapsed={store.sidebarCollapsed}
                      showDraftsOption={canWriteOwn}
                    />
                  </Box>

                  <Box className={s.calendar_page__content}>
                    <DraftWarningBanner />
                    {(isViewingSelf ? store.weekDataWithDrafts : store.weekDataWithOtherLogs) && (
                      <CalendarViewWidget
                        weekData={
                          isViewingSelf ? store.weekDataWithDrafts! : store.weekDataWithOtherLogs!
                        }
                        onEventClick={handleEventClick}
                        onAddLogClick={isLogsReadOnly ? undefined : handleAddLogClick}
                        dailyWorkMinutes={store.dailyWorkMinutes}
                        isBlurred={store.isCaldavNotConfigured}
                        showCreateDraftOption={canWriteOwn}
                        readOnly={isLogsReadOnly}
                      />
                    )}

                    {store.isLoadingEvents && (
                      <Box className={s.calendar_page__loading_overlay}>
                        <CircularProgress size={28} />
                      </Box>
                    )}

                    {store.isCaldavNotConfigured && (
                      <CalDavRequiredOverlay
                        onSettingsClick={() => navigate(routerUrls.settings)}
                      />
                    )}
                  </Box>
                </Box>
              )}

              {/* Event details modal */}
              <EventDetailsModal
                open={store.selectedEvent !== null}
                onClose={store.clearSelectedEvent}
                event={store.selectedEvent}
                attendees={store.selectedEventAttendees}
                attendeesLoading={store.isAttendeesLoading}
                onLogTime={handleLogTime}
                onEditWorklog={handleEditWorklog}
                onDeleteWorklog={handleDeleteWorklog}
                readOnly={isLogsReadOnly}
              />

              {/* Other logs detail panel */}
              <OtherLogsDetailPanel
                open={store.selectedLogGroup !== null}
                group={store.selectedLogGroup}
                onClose={store.clearLogGroup}
                onEditLog={handleEditOtherLog}
              />

              {/* Time log form from calendar event */}
              {store.logFormEvent && store.logFormDefaults && (
                <AddLog
                  open
                  onClose={() => {
                    store.closeLogForm();
                    logCheckerModel.reset();
                  }}
                  onSubmit={handleLogFormSubmit}
                  day={DateTime.fromISO(store.logFormDefaults.startDate)}
                  taskKey={store.logFormDefaults.taskKey ?? undefined}
                  taskTitle={
                    store.logFormTaskLabel ? store.logFormTaskLabel.split(' — ')[1] : undefined
                  }
                  isTaskSelected={!!store.logFormTaskLabel}
                  isSingleEntry
                  initialStartTime={store.logFormDefaults.startTime}
                  initialDuration={store.logFormDefaults.duration}
                  initialComment={store.logFormDefaults.comment}
                  // У участников встреч только uid Яндекса: в нашем учёте за них не предзаполняем
                  calendarAttendees={
                    mode === LogsMode.v2 ? undefined : store.logFormDefaults.attendees
                  }
                  forSelfOnly={!store.logFormForAll}
                  companionPanel={<AttendeeLogCheckerPanel model={logCheckerModel} />}
                  onUsersChange={handleUsersChange}
                  onTaskKeyChange={handleTaskKeyChange}
                  autoShowCompanion={store.logFormForAll}
                />
              )}

              {/* Quick add time log from day header */}
              {store.quickAddDate && (
                <AddLog
                  open
                  onClose={store.closeQuickAdd}
                  onSubmit={handleSimpleAddSubmit}
                  day={store.quickAddDate}
                  isSingleEntry
                  initialStartTime="12:00"
                />
              )}

              {/* Drag-to-log form */}
              {store.dragLogData && (
                <AddLog
                  open
                  onClose={store.closeDragLog}
                  onSubmit={handleSimpleAddSubmit}
                  day={store.dragLogData.date}
                  isSingleEntry
                  initialStartTime={store.dragLogData.startTime}
                  initialDuration={store.dragLogData.duration}
                />
              )}

              {/* Edit other log modal */}
              {store.editingLog && (
                <EditLog
                  open
                  log={store.editingLog}
                  onClose={store.closeEditLog}
                  onDelete={() => {
                    const log = store.editingLog;

                    if (!log) return;

                    store.closeEditLog();
                    modalManagerStore.open('deleteLog', log);
                  }}
                  onSubmit={handleEditLogSubmit}
                />
              )}

              {/* Edit draft form */}
              {store.selectedDraft && (
                <DraftForm
                  mode="edit"
                  draft={store.selectedDraft}
                  onClose={() => {
                    store.closeDraftEdit();
                    store.loadWeekData();
                  }}
                />
              )}

              {/* Drag-to-create-draft form (только при просмотре себя) */}
              {canWriteOwn && store.dragCreateDraftData && (
                <DraftForm
                  mode="create"
                  day={store.dragCreateDraftData.date}
                  initialStartTime={store.dragCreateDraftData.startTime}
                  initialDuration={store.dragCreateDraftData.duration}
                  onClose={store.closeDragCreateDraft}
                />
              )}

              {/* No calendars selected notice */}
              <NoCalendarsSelectedNotice
                open={store.isNoCalendarsSelected && showNoCalendarsNotice}
                onClose={() => setShowNoCalendarsNotice(false)}
                onSettingsClick={() => navigate(routerUrls.settings)}
              />
            </Box>
          </TaskStoreProvider>
        </TimeStatsProvider>
      </DraftTimelogProvider>
    </TimeLogStoreProvider>
  );
});

const CalendarPageContent: React.FC = observer(() => {
  return (
    <FavoriteUsersProvider>
      <GlobalUserFilterProvider>
        <CalendarPageBody />
      </GlobalUserFilterProvider>
    </FavoriteUsersProvider>
  );
});

export const CalendarPage: React.FC = () => {
  return (
    <CalendarViewProvider>
      <CalendarPageContent />
    </CalendarViewProvider>
  );
};
